import { useContext, useEffect, useState } from 'react';
import { AppContext } from 'contexts/AppContext';
import { useVoiceData } from 'hooks/useVoiceData';
import { formatUtcDateTime } from 'utils/helperFunctions';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Mic, Timer, Plug, Info, AlertTriangle } from 'lucide-react';

/**
 * Voice — hands-free conversations.
 *
 * A voice turn is stored in the same interactions and messages rows a text turn
 * is; the voice facts ride `metadata` jsonb. There is no voice table, which is
 * why this shipped with no migration and no app release.
 *
 * Session length is max(voice_session_ms) across a session's turns: every turn
 * carries the elapsed time at the moment it closed.
 *
 * ⚠️ ZEROS ARE EXPECTED BEFORE THE FIRST VOICE RELEASE and are not a bug.
 * Voice data starts at deploy — there is no history to backfill — so an empty
 * range is a true statement about the past, not a broken card. The empty state
 * below says so, because an unexplained blank panel is how a reader concludes
 * the tab is broken and stops trusting the rest of it.
 */

const fmt = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString());

/** ms → human. Sub-second stays in ms; a "0.4s" reads as rounded-to-nothing. */
const dur = (ms) => {
  if (ms === null || ms === undefined) return '—';
  const n = Number(ms);
  if (n < 1000) return `${Math.round(n)}ms`;
  if (n < 60000) return `${(n / 1000).toFixed(1)}s`;
  const m = Math.floor(n / 60000);
  return `${m}m ${Math.round((n % 60000) / 1000)}s`;
};

/** Headline number. Never renders a null as 0. */
function Stat({ label, value, sub, note }) {
  return (
    <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 p-4">
      <div className="text-xs text-gray-500 mb-1">{label}</div>
      <div className="text-2xl font-medium">{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
      {note && <div className="text-[11px] text-amber-700 dark:text-amber-500 mt-1.5">{note}</div>}
    </div>
  );
}

/** A metric with no honest source. Dimmed, labelled, reason stated. */
function PendingStat({ label, badge, reason }) {
  return (
    <div className="rounded-lg border border-dashed border-zinc-300 dark:border-zinc-700 p-4 opacity-70">
      <div className="text-xs text-gray-500 mb-1 flex items-center gap-2">
        {label}
        <Badge variant="outline" className="font-normal text-[10px] px-1.5 py-0 whitespace-nowrap">
          {badge}
        </Badge>
      </div>
      <div className="text-2xl font-medium text-gray-400">—</div>
      <div className="text-xs text-gray-500 mt-1">{reason}</div>
    </div>
  );
}

export default function VoiceTab() {
  const { selectedClient, dateRange } = useContext(AppContext);
  const {
    summary, summaryLoading, summaryError, fetchSummary,
    sessions, sessionsLoading, sessionsError, fetchSessions,
  } = useVoiceData();

  // Off by default, same as Outcomes: the tab describes ALL traffic unless you
  // say otherwise. Defaulting to ON would silently change what every figure
  // means without the reader choosing it.
  const [audience, setAudience] = useState('all');
  const excludeInternal = audience === 'customers';
  const internalOnly = audience === 'internal';

  useEffect(() => {
    fetchSummary({ excludeInternal, internalOnly });
    fetchSessions({ excludeInternal, internalOnly });
  }, [fetchSummary, fetchSessions, audience, excludeInternal, internalOnly,
      selectedClient, dateRange]);

  // "No barge-ins" and "barge-ins were never recorded" are different facts, and
  // the API returns both counts precisely so this card can tell them apart.
  const bargeRecorded = summary?.barge_in_recorded_turns ?? 0;
  const outputRecorded = summary?.output_tokens_recorded ?? 0;
  // The Live API reports no output-token field at all, so adk-v2 derives output
  // as total - prompt and flags every turn it inferred. This is how many of the
  // turns behind the figure below were inferred rather than measured. The writer
  // has always set that flag; nothing read it until now, so this number was being
  // presented as if it had been measured.
  const outputDerived = summary?.output_tokens_derived_turns ?? 0;

  return (
    <div className="flex flex-col gap-5 mt-4">
      <div className="flex items-center gap-2 flex-wrap">
        {[
          { v: 'all', l: 'All Traffic' },
          { v: 'customers', l: 'Customers Only' },
          { v: 'internal', l: 'Internal Only' },
        ].map((o) => (
          <Button key={o.v} size="sm"
                  variant={audience === o.v ? 'default' : 'outline'}
                  onClick={() => setAudience(o.v)}>
            {o.l}
          </Button>
        ))}
        <span className="text-xs text-gray-500">
          {audience === 'customers'
            ? 'Excluding the internal accounts. Voice carries no page address, so staging and local origins cannot be excluded here the way they are on Outcomes.'
            : audience === 'internal'
              ? 'Internal accounts only.'
              : 'Including internal accounts.'}
        </span>
      </div>

      {summaryError && <div className="text-sm text-red-600">{summaryError}</div>}
      {summaryLoading && <div className="text-sm text-gray-500 py-6">Loading…</div>}

      {!summaryLoading && summary && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
            <Stat
              label="Voice Sessions"
              value={fmt(summary.sessions)}
              sub={`${fmt(summary.users)} distinct users`}
            />
            <Stat
              label="Voice Turns"
              value={fmt(summary.turns)}
              sub={
                summary.turns_per_session
                  ? `${summary.turns_per_session} per session`
                  : 'per-session average unavailable'
              }
            />
            <Stat
              label="Session Length"
              value={dur(summary.session_ms_p50)}
              sub={`p50 · p95 ${dur(summary.session_ms_p95)}`}
              note="Longest turn's elapsed time, not a sum"
            />
            <Stat
              label="Time to Connect"
              value={dur(summary.connect_ms_p50)}
              sub={`p50 · p95 ${dur(summary.connect_ms_p95)}`}
            />
            {summary.token_cost_usd != null && (
              <Stat
                label="Est. Token Cost"
                value={`$${Number(summary.token_cost_usd).toFixed(2)}`}
                sub="at the configured per-token price"
                note={outputDerived > 0
                  ? 'The output side is partly inferred — see the Tokens card.'
                  : undefined}
              />
            )}
            {outputRecorded > 0 ? (
              <Stat
                label="Tokens"
                value={fmt(summary.total_tokens)}
                sub={`${fmt(summary.prompt_tokens)} prompt · ${fmt(summary.output_tokens)} output`}
                note={
                  outputDerived > 0
                    ? `Output inferred on ${fmt(outputDerived)} of ${fmt(outputRecorded)} turns: the Live API reports no output field, so it is total minus prompt.`
                    : undefined
                }
              />
            ) : (
              <PendingStat
                label="Token Cost"
                badge="Not Recorded"
                reason="No turn in this range recorded output tokens. The Live API reports no output field for native audio, so adk-v2 derives it from the total — a range predating that change has nothing to show."
              />
            )}
          </div>

          {/* Dictation is the OTHER voice: speech-to-text into the edit box.
              A correction is a stored transcription that differs from what was
              sent -- the only visible evidence of an STT error -- and the
              denominator is transcript-bearing turns only, so an absent
              transcript never counts as "not corrected". Mixed sessions used
              voice AND typed; direction is not recorded, so the label says
              mixed, not "fell back". */}
          {(summary.dictated_turns > 0 || summary.mixed_modality_sessions > 0) && (
            <div className="flex gap-2 rounded-md border border-zinc-300 dark:border-zinc-700 p-2.5 text-xs text-gray-600 dark:text-gray-300">
              <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>
                Dictation: <strong>{summary.dictated_turns}</strong> turn{summary.dictated_turns === 1 ? '' : 's'}
                {summary.dictated_with_transcript > 0 && (
                  <> — <strong>{summary.dictated_corrected}</strong> of{' '}
                  <strong>{summary.dictated_with_transcript}</strong> with a stored transcript were
                  corrected before sending</>
                )}
                {summary.mixed_modality_sessions > 0 && (
                  <>. <strong>{summary.mixed_modality_sessions}</strong> session{summary.mixed_modality_sessions === 1 ? '' : 's'} used
                  both voice and typing.</>
                )}
              </span>
            </div>
          )}

          {/* Barge-in: reported only when the field was actually captured. A
              card reading "0 barge-ins" when the field was never written is a
              confident wrong number, which is the failure this console keeps
              finding elsewhere. */}
          <div className="flex gap-2 rounded-md border border-zinc-300 dark:border-zinc-700 p-2.5 text-xs text-gray-600 dark:text-gray-300">
            <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            {bargeRecorded > 0 ? (
              <span>
                <strong>{fmt(summary.barge_in_turns)}</strong> of{' '}
                <strong>{fmt(bargeRecorded)}</strong> turns were interrupted by the
                user before the assistant finished speaking.
              </span>
            ) : (
              <span>
                <strong>Interruptions aren't recorded for this range.</strong> The
                signal exists on the model connection, but nothing wrote it onto the
                turn until the voice bridge started reading it, so this reads as
                unknown rather than as zero.
              </span>
            )}
          </div>

          {summary.models?.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Mic className="h-4 w-4" />
                  Models
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-500 border-b border-zinc-200 dark:border-zinc-800">
                      <th className="text-left font-normal py-1.5">Model</th>
                      <th className="text-right font-normal py-1.5">Sessions</th>
                      <th className="text-right font-normal py-1.5">Turns</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.models.map((m) => (
                      <tr key={m.model} className="border-b border-zinc-100 dark:border-zinc-900 last:border-0">
                        <td className="py-1.5 font-mono text-xs">{m.model}</td>
                        <td className="text-right tabular-nums">{fmt(m.sessions)}</td>
                        <td className="text-right tabular-nums">{fmt(m.turns)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* ── sessions ───────────────────────────────────────────────────── */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Timer className="h-4 w-4" />
            Recent Sessions
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {sessionsError && (
            <div className="flex gap-2 text-sm text-red-600 py-4">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{sessionsError}</span>
            </div>
          )}
          {!sessionsError && sessionsLoading && (
            <div className="text-sm text-gray-500 py-6">Loading…</div>
          )}
          {!sessionsError && !sessionsLoading && sessions.length === 0 && (
            <div className="text-sm text-gray-500 py-6">
              No voice sessions in this range.{' '}
              <span className="text-gray-400">
                Voice data starts at the release that enabled it — there is no
                history before then to show.
              </span>
            </div>
          )}
          {!sessionsError && !sessionsLoading && sessions.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-500 border-b border-zinc-200 dark:border-zinc-800">
                    <th className="text-left font-normal py-1.5">Started</th>
                    <th className="text-left font-normal py-1.5">Session</th>
                    <th className="text-right font-normal py-1.5">Turns</th>
                    <th className="text-right font-normal py-1.5">Length</th>
                    <th className="text-right font-normal py-1.5">Connect</th>
                    <th className="text-left font-normal py-1.5 pl-4">Model</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((s) => (
                    <tr key={s.session_id} className="border-b border-zinc-100 dark:border-zinc-900 last:border-0">
                      <td className="py-1.5 whitespace-nowrap">
                        {formatUtcDateTime(s.started_at)}
                      </td>
                      <td className="py-1.5 font-mono text-xs truncate max-w-[160px]" title={s.session_id}>
                        {s.session_id}
                      </td>
                      <td className="text-right tabular-nums">{fmt(s.turns)}</td>
                      <td className="text-right tabular-nums">{dur(s.session_ms)}</td>
                      <td className="text-right tabular-nums">{dur(s.connect_ms)}</td>
                      <td className="py-1.5 pl-4 font-mono text-xs truncate max-w-[200px]" title={s.model}>
                        {s.model || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex gap-2 rounded-md border border-zinc-300 dark:border-zinc-700 p-2.5 text-xs text-gray-600 dark:text-gray-300">
        <Plug className="h-3.5 w-3.5 shrink-0 mt-0.5" />
        <span>
          Voice turns are stored in the same rows as text conversations and are
          told apart by a per-turn marker. Overview counts them in Conversations
          and Turns and shows the voice share beneath each, and its response
          times exclude voice because spoken-answer time is a different quantity.
          Quality does not separate the two yet.
        </span>
      </div>
    </div>
  );
}
